import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import type { Market } from '../dataProvider/types';
import { formatMoney } from '../cashbacks/formatMoney';
import type { SubscriptionsBand as Band } from './subscriptionsModel';
import { SubscriptionsTable } from './SubscriptionsTable';

/**
 * One full-width market band: the MRR headline with the at-risk figure beside it,
 * the four-state breakdown, then the member list. Single-currency throughout (no
 * cross-market total anywhere, the Earnings rule). MRR is the honest count of
 * active subscriptions times the period price; the pending_cancel count sits
 * BESIDE it as next period's likely churn, never blended in.
 */

const MARKET_NAME: Record<Market, string> = {
  uk: 'United Kingdom',
  us: 'United States',
  ie: 'Ireland',
};

const Stat = ({
  label,
  value,
  testId,
}: {
  label: string;
  value: string | number;
  testId: string;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography
      variant="body1"
      component="div"
      sx={{ fontWeight: 600 }}
      data-testid={testId}
    >
      {value}
    </Typography>
  </Box>
);

export const SubscriptionsBand = ({
  band,
  now,
}: {
  band: Band;
  now: number;
}) => (
  <Card
    variant="outlined"
    data-testid={`sub-band-${band.market}`}
    sx={{ mb: 3 }}
  >
    <CardContent>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        {MARKET_NAME[band.market]}
      </Typography>

      {/* MRR headline + the at-risk (pending_cancel) figure beside it. */}
      <Stack
        direction="row"
        spacing={3}
        alignItems="flex-end"
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            Monthly recurring revenue
          </Typography>
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 700 }}
            data-testid={`sub-mrr-${band.market}`}
          >
            {formatMoney(band.mrr, band.currency)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {band.activeCount} active x{' '}
            {formatMoney(band.periodPrice, band.currency)}
          </Typography>
        </Box>
        <Chip
          color="warning"
          variant="outlined"
          data-testid={`sub-atrisk-${band.market}`}
          label={`${band.pendingCancelCount} at risk (pending cancel)`}
        />
      </Stack>

      {/* The four-state breakdown. lapsed and lapsed_insufficient are separate
          counts so the recoverable group is always distinguishable. */}
      <Stack
        direction="row"
        spacing={4}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 3 }}
      >
        <Stat
          label="Active"
          value={band.activeCount}
          testId={`sub-stat-${band.market}-active`}
        />
        <Stat
          label="Pending cancel"
          value={band.pendingCancelCount}
          testId={`sub-stat-${band.market}-pending`}
        />
        <Stat
          label="Lapsed"
          value={band.lapsedCount}
          testId={`sub-stat-${band.market}-lapsed`}
        />
        <Stat
          label="Lapsed (insufficient funds)"
          value={band.lapsedInsufficientCount}
          testId={`sub-stat-${band.market}-insufficient`}
        />
      </Stack>

      <SubscriptionsTable members={band.members} now={now} />
    </CardContent>
  </Card>
);
